/** New normalized lab reference-data catalog — /api/lab-catalog/... (additive; the older, simpler
 *  `inpatient.getLabMetricTemplates()` lookup is untouched and still used as the primary fallback
 *  for ad-hoc result-entry rows, see LabTestEntry.tsx). This is the richer, multi-dimensional
 *  reference-range engine described in the lab reference-range platform design. */
import { fetchApi } from '../client';

export interface LabCatalogMetric {
  id: number;
  code: string;
  name: string;
  shortName?: string;
  category?: string;
  resultType: string;
  defaultUnit?: string;
  loincCode?: string;
  needsTerminologyMapping: boolean;
  isCalculated: boolean;
  /** Real, already-stored LabMetric columns (Phase 12 detail screen) — populated by getMetric/
   *  searchMetricsAdmin, left undefined by the older unpaginated searchMetrics typeahead. */
  aliases?: string;
  defaultSpecimenType?: string;
  notes?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface LabCatalogReferenceRangeSelection {
  ageMatched: boolean;
  sexMatched: boolean;
  pregnancyMatched: boolean;
  specimenMatched: boolean;
  methodMatched: boolean;
  laboratoryMatched: boolean;
  source: 'LABORATORY_SPECIFIC' | 'CLINIC_SPECIFIC' | 'GLOBAL_DEFAULT' | 'NONE';
  reason: string;
}

export interface LabCatalogReferenceRange {
  lower?: number;
  upper?: number;
  qualitativeValue?: string;
  unit?: string;
  criticalLow?: number;
  criticalHigh?: number;
  sourceName?: string;
  sourceUrl?: string;
  notes?: string;
  selection: LabCatalogReferenceRangeSelection;
}

export interface LabPatientContextParams {
  age?: number;
  ageUnit?: 'DAYS' | 'WEEKS' | 'MONTHS' | 'YEARS';
  sex?: 'ALL' | 'MALE' | 'FEMALE' | 'UNKNOWN';
  pregnancyStatus?: 'NOT_APPLICABLE' | 'NOT_PREGNANT' | 'PREGNANT' | 'POSTPARTUM' | 'UNKNOWN';
  trimester?: 'FIRST' | 'SECOND' | 'THIRD';
  specimenTypeId?: number;
  methodId?: number;
  unitId?: number;
  fasting?: boolean;
  labId?: string;
}

export interface LabCatalogLookup {
  id: number;
  code: string;
  displayName?: string;
  name?: string;
  description?: string;
  symbol?: string;
  ucumCode?: string;
  quantityType?: string;
  isActive?: boolean;
  parentGroupId?: number | null;
  sortOrder?: number | null;
}

export interface LabCatalogQualitativeValue {
  id: number;
  metricId: number;
  clinicId?: string | null;
  valueCode: string;
  displayLabel: string;
  isNormal?: boolean | null;
  isCritical?: boolean | null;
  sortOrder?: number;
}

export interface CreateReferenceRangeOverride {
  unitId?: number;
  specimenTypeId?: number;
  methodId?: number;
  ageMin?: number;
  ageMax?: number;
  ageUnit?: 'DAYS' | 'WEEKS' | 'MONTHS' | 'YEARS';
  sex?: 'ALL' | 'MALE' | 'FEMALE' | 'UNKNOWN';
  pregnancyStatus?: 'NOT_APPLICABLE' | 'NOT_PREGNANT' | 'PREGNANT' | 'POSTPARTUM' | 'UNKNOWN';
  trimester?: 'FIRST' | 'SECOND' | 'THIRD';
  fastingRequired?: boolean;
  lowerValue?: number;
  upperValue?: number;
  criticalLowThreshold?: number;
  criticalHighThreshold?: number;
  qualitativeValue?: string;
  valueType?: 'NUMERIC_RANGE' | 'ONE_SIDED_LOW' | 'ONE_SIDED_HIGH' | 'QUALITATIVE';
  sourceName: string;
  sourceUrl?: string;
  sourceVersion?: string;
  notes?: string;
}

function qs(params: Record<string, unknown>): string {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

// ── Admin catalog (Phase 12 — Lab Administration screens) ────────────────────────────────────
// Everything below is additive read/write support for the Lab Administration UI
// (src/pages/admin/labCatalog/) — none of it is used by the existing read-only result-entry flow
// above. See LabReferenceDataController for the backend side of each call.

export interface PagedResponse<T> {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}

/** Backend's generic {success, data, message} wrapper — distinct from the frontend fetchApi
 *  envelope ({@link ApiResponse}). Every mutating /lab-catalog endpoint returns this shape. */
export interface BackendActionResult<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface LabCatalogReferenceRangeRow {
  id: number;
  version?: number;
  metricId: number;
  clinicId?: string | null;
  labId?: string | null;
  unitId?: number | null;
  specimenTypeId?: number | null;
  methodId?: number | null;
  ageMin?: number | null;
  ageMax?: number | null;
  ageUnit?: 'DAYS' | 'WEEKS' | 'MONTHS' | 'YEARS';
  sex?: 'ALL' | 'MALE' | 'FEMALE' | 'UNKNOWN';
  pregnancyStatus?: 'NOT_APPLICABLE' | 'NOT_PREGNANT' | 'PREGNANT' | 'POSTPARTUM' | 'UNKNOWN';
  trimester?: 'FIRST' | 'SECOND' | 'THIRD' | null;
  fastingRequired?: boolean | null;
  valueType?: 'NUMERIC_RANGE' | 'ONE_SIDED_LOW' | 'ONE_SIDED_HIGH' | 'QUALITATIVE';
  lowerValue?: number | null;
  upperValue?: number | null;
  qualitativeValue?: string | null;
  criticalLowThreshold?: number | null;
  criticalHighThreshold?: number | null;
  sourceName: string;
  sourceUrl?: string | null;
  sourceVersion?: string | null;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  status?: 'ACTIVE' | 'RETIRED' | 'DEPRECATED' | 'DRAFT' | string;
  notes?: string | null;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface LabCatalogAuditLogRow {
  id: number;
  referenceRangeId: number;
  metricId: number;
  clinicId?: string | null;
  action: 'CREATE' | 'UPDATE' | 'ACTIVATE' | 'DEACTIVATE';
  changedBy?: string;
  changedByRole?: string;
  oldValuesJson?: string;
  newValuesJson?: string;
  reason?: string;
  changedAt: string;
}

export interface LabCalculationDefinitionRow {
  id: number;
  outputMetricId: number;
  formulaName: string;
  formulaVersion?: string;
  formulaDescription?: string;
  equationReference?: string;
  sourceName?: string;
  sourceUrl?: string;
  effectiveFrom?: string;
  effectiveTo?: string;
  isActive: boolean;
}

export interface LabTerminologyMappingRow {
  id: number;
  metricId?: number | null;
  system: 'LOINC' | 'SNOMED_CT' | 'LOCAL';
  code: string;
  display?: string;
  longCommonName?: string;
  shortName?: string;
  component?: string;
  property?: string;
  loincSystem?: string;
  method?: string;
  status: 'ACTIVE' | 'RETIRED' | 'DEPRECATED' | 'DRAFT' | string;
  version?: string;
  source?: string;
  isPrimary?: boolean;
  notes?: string;
}

export interface AdminMetricSearchParams {
  search?: string;
  categoryId?: number;
  resultType?: string;
  hasLoinc?: boolean;
  hasRange?: boolean;
  activeOnly?: boolean;
  page?: number;
  pageSize?: number;
  sortBy?: 'name' | 'code' | 'resultType' | 'category';
  sortDir?: 'asc' | 'desc';
}

export interface LabCatalogCoverage {
  activeMetrics: number;
  metricsWithRange: number;
  metricsWithoutRange: number;
  metricsWithLoinc: number;
  metricsWithoutLoinc: number;
}

export const labCatalogAdminModule = {
  searchMetricsAdmin: (params: AdminMetricSearchParams = {}) =>
    fetchApi<PagedResponse<LabCatalogMetric>>(`/api/lab-catalog/admin/metrics${qs(params as Record<string, unknown>)}`),

  /** Catalog-completeness roll-up (Phase 15) — surfaces "which metrics still lack a sourced
   *  reference range" as a visible, trackable number rather than a silent gap. Never a reason to
   *  fabricate a range to make the number look better — see LabReferenceRange class javadoc. */
  getMetricCoverage: () =>
    fetchApi<LabCatalogCoverage>('/api/lab-catalog/admin/metrics/coverage'),

  getMetricTerminology: (metricId: number) =>
    fetchApi<LabTerminologyMappingRow[]>(`/api/lab-catalog/metrics/${metricId}/terminology`),

  searchTerminology: (params: { search?: string; system?: string; linkedOnly?: boolean; page?: number; pageSize?: number } = {}) =>
    fetchApi<PagedResponse<LabTerminologyMappingRow>>(`/api/lab-catalog/terminology${qs(params as Record<string, unknown>)}`),

  listCalculationDefinitions: () =>
    fetchApi<LabCalculationDefinitionRow[]>('/api/lab-catalog/calculation-definitions'),

  listReferenceRangesForMetric: (metricId: number) =>
    fetchApi<LabCatalogReferenceRangeRow[]>(`/api/lab-catalog/metrics/${metricId}/reference-ranges`),

  updateReferenceRange: (rangeId: number, data: CreateReferenceRangeOverride & { reason?: string }) =>
    fetchApi<BackendActionResult<LabCatalogReferenceRangeRow>>(`/api/lab-catalog/reference-ranges/${rangeId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  setReferenceRangeStatus: (rangeId: number, active: boolean, reason?: string) =>
    fetchApi<BackendActionResult<LabCatalogReferenceRangeRow>>(`/api/lab-catalog/reference-ranges/${rangeId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ active, reason }),
    }),

  getReferenceRangeAudit: (rangeId: number) =>
    fetchApi<LabCatalogAuditLogRow[]>(`/api/lab-catalog/reference-ranges/${rangeId}/audit`),

  setQualitativeValueCritical: (id: number, critical: boolean) =>
    fetchApi<BackendActionResult<LabCatalogQualitativeValue & { isCritical: boolean }>>(`/api/lab-catalog/qualitative-values/${id}/critical`, {
      method: 'PATCH',
      body: JSON.stringify({ critical }),
    }),
};

export const labCatalogModule = {
  listTestGroups: () => fetchApi<LabCatalogLookup[]>('/api/lab-catalog/test-groups'),
  listUnits: () => fetchApi<LabCatalogLookup[]>('/api/lab-catalog/units'),
  listSpecimenTypes: () => fetchApi<LabCatalogLookup[]>('/api/lab-catalog/specimen-types'),
  listMethods: () => fetchApi<LabCatalogLookup[]>('/api/lab-catalog/methods'),

  searchMetrics: (search?: string, categoryId?: number) =>
    fetchApi<LabCatalogMetric[]>(`/api/lab-catalog/metrics${qs({ search, categoryId })}`),

  getMetric: (id: number) => fetchApi<LabCatalogMetric>(`/api/lab-catalog/metrics/${id}`),

  getQualitativeValues: (id: number) =>
    fetchApi<LabCatalogQualitativeValue[]>(`/api/lab-catalog/metrics/${id}/qualitative-values`),

  /** The core patient-aware lookup — every param optional; omitting one just means the engine
   *  can't use a range specific to that dimension (see ReferenceRangeService, it never guesses). */
  getReferenceRange: (id: number, ctx: LabPatientContextParams = {}) =>
    fetchApi<LabCatalogReferenceRange>(`/api/lab-catalog/metrics/${id}/reference-range${qs(ctx as Record<string, unknown>)}`),

  createClinicOverride: (id: number, data: CreateReferenceRangeOverride) =>
    fetchApi<{ id: number }>(`/api/lab-catalog/metrics/${id}/reference-ranges`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  classify: (id: number, data: { numericValue?: number; qualitativeValue?: string } & LabPatientContextParams & { upstreamFlaggedCritical?: boolean }) =>
    fetchApi<string>(`/api/lab-catalog/metrics/${id}/classify`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};
